import { useState, type ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Plus } from "lucide-react";
import { FirstRunGuide } from "~/components/first-run-guide";
import { Notice } from "~/components/notice";
import { ParcelCard } from "~/components/parcel-card";
import { PaySheet } from "~/components/pay-sheet";
import { SearchInput } from "~/components/search-input";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { api } from "~/lib/api";
import { useMe } from "~/lib/auth";
import { formatKES, toNum } from "~/lib/format";
import { isPayable, needsCustomer, parcelTitle, statusHeadline } from "~/lib/parcel-status";
import type { IParcel } from "~/lib/proto";

export const Route = createFileRoute("/_authed/_portal/")({
  component: Parcels,
});

const SEARCH_FROM = 6;

function Parcels() {
  const { data: me } = useMe();
  const [query, setQuery] = useState("");
  const [payingFor, setPayingFor] = useState<IParcel[]>([]);
  const [payOpen, setPayOpen] = useState(false);

  const active = useQuery({ queryKey: ["parcels"], queryFn: () => api.listParcels(false) });
  const delivered = useQuery({ queryKey: ["parcels", "archived"], queryFn: () => api.listParcels(true) });
  const unread = useQuery({ queryKey: ["my-unread"], queryFn: () => api.unread(), staleTime: 3000 });

  const all = active.data ?? [];
  const deliveredCount = delivered.data?.length ?? 0;

  if (active.data && delivered.data && all.length === 0 && deliveredCount === 0) {
    return <FirstRunGuide name={me?.name} />;
  }

  const term = query.trim().toLowerCase();
  const shown = term
    ? all.filter((p) => [parcelTitle(p), p.trackingNumber, statusHeadline(p)].some((text) => text?.toLowerCase().includes(term)))
    : all;
  const needsYou = shown.filter(needsCustomer);
  const onTheWay = shown.filter((p) => !needsCustomer(p));
  const payable = all.filter(isPayable);
  const payableTotal = payable.reduce((sum, p) => sum + toNum(p.amountChargedKes), 0);

  function pay(parcels: IParcel[]) {
    setPayingFor(parcels);
    setPayOpen(true);
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-3">
        <h1 className="type-title">Parcels</h1>
        <Button asChild size="lg" className="hidden md:inline-flex">
          <Link to="/register">
            <Plus /> Add parcel
          </Link>
        </Button>
      </header>

      {all.length >= SEARCH_FROM && (
        <SearchInput density="comfortable" value={query} onChange={setQuery} placeholder="Search by name or tracking number" />
      )}

      {active.isLoading ? (
        <div className="space-y-3" aria-busy>
          <Skeleton className="h-36 rounded-xl" />
          <Skeleton className="h-36 rounded-xl" />
        </div>
      ) : active.isError ? (
        <Notice title="We could not load your parcels" body="Check your connection, then try again.">
          <Button variant="outline" className="h-11 px-5 text-[15px]" onClick={() => void active.refetch()}>
            Try again
          </Button>
        </Notice>
      ) : all.length === 0 ? (
        <Notice title="Nothing on the way" body="Ordered something abroad? Add its tracking number and we take it from there." />
      ) : shown.length === 0 ? (
        <Notice title="No parcels match that" body="Try the item name or the tracking number." />
      ) : (
        <>
          {needsYou.length > 0 && (
            <Section
              title="Needs you"
              action={
                payable.length > 1 &&
                !term && (
                  <Button className="h-11 px-4 text-[15px]" onClick={() => pay(payable)}>
                    Pay all {formatKES(payableTotal)}
                  </Button>
                )
              }
            >
              {needsYou.map((parcel) => (
                <ParcelCard
                  key={parcel.id}
                  parcel={parcel}
                  unread={unread.data?.parcel[parcel.id ?? ""] ?? 0}
                  action={
                    isPayable(parcel) && (
                      <Button
                        variant={payable.length > 1 ? "outline" : "default"}
                        className="h-11 w-full text-[15px]"
                        onClick={() => pay([parcel])}
                      >
                        Pay {formatKES(parcel.amountChargedKes)}
                      </Button>
                    )
                  }
                />
              ))}
            </Section>
          )}
          {onTheWay.length > 0 && (
            <Section title="On the way">
              {onTheWay.map((parcel) => (
                <ParcelCard key={parcel.id} parcel={parcel} unread={unread.data?.parcel[parcel.id ?? ""] ?? 0} />
              ))}
            </Section>
          )}
        </>
      )}

      {deliveredCount > 0 && (
        <Link
          to="/history"
          className="border-hairline bg-card hover:bg-accent/30 flex min-h-14 items-center justify-between gap-3 rounded-xl border px-4 transition-colors"
        >
          <span className="text-[15px] font-medium">Delivered</span>
          <span className="text-muted-foreground flex items-center gap-1 text-[15px]">
            <span className="tabular">{deliveredCount}</span>
            <ChevronRight className="size-5" />
          </span>
        </Link>
      )}

      <div className="h-12 md:hidden" aria-hidden />
      <div
        className="pointer-events-none fixed inset-x-0 z-20 flex justify-center px-4 md:hidden"
        style={{ bottom: "calc(env(safe-area-inset-bottom) + 4.5rem)" }}
      >
        <Button asChild size="touch" className="shadow-foreground/15 pointer-events-auto rounded-full shadow-lg">
          <Link to="/register">
            <Plus /> Add parcel
          </Link>
        </Button>
      </div>

      <PaySheet open={payOpen} onOpenChange={setPayOpen} parcels={payingFor} />
    </div>
  );
}

function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <div className="flex min-h-11 items-center justify-between gap-3">
        <h2 className="text-muted-foreground text-[13px] font-semibold tracking-[0.05em] uppercase">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}
